import React, { useEffect, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './PoolState.scss'

import { formatString } from 'Helpers/formatString'
import { TimeConverter } from 'Helpers/TimeConverter'
import { plpType } from 'Models/plpType'
import financialTransactionsService, {
  type FinancialTransaction,
} from 'Services/financialTransactionsService'
import plpService from 'Services/plpService'
import tokenPoolService, { type TokenPoolSnapshot } from 'Services/tokenPoolService'

export type PoolTab = 'assigned' | 'unassigned'

const formatNairaMajor = (value: number | undefined | null) => {
  if (value === undefined || value === null) return '-'
  return `₦${value.toLocaleString()}`
}

const shortenValue = (value: string | undefined, start = 8, end = 4) => {
  if (!value) return undefined
  if (value.length <= start + end + 3) return value
  return `${value.slice(0, start)}...${value.slice(-end)}`
}

const formatTransactionAmount = (transaction: FinancialTransaction) => {
  const amount = transaction.amountMajor
  if (amount === undefined || amount === null) return null

  const sign = transaction.flowType === 'in' ? '+' : transaction.flowType === 'out' ? '−' : ''
  const absoluteAmount = Math.abs(amount)
  const currencyPrefix =
    !transaction.currency || transaction.currency === 'NGN' ? '₦' : `${transaction.currency} `

  return `${sign}${currencyPrefix}${absoluteAmount.toLocaleString()}`
}

const getTransactionDotVariant = (transaction: FinancialTransaction) => {
  if (transaction.flowType === 'in') return 'green'
  if (transaction.flowType === 'out') return 'orange'
  return 'blue'
}

const getTransactionPrimaryText = (transaction: FinancialTransaction) => {
  const baseText =
    transaction.description?.trim() ||
    (transaction.type ? formatString(transaction.type) : '') ||
    'Transaction'
  const amountText = formatTransactionAmount(transaction)
  return amountText ? `${baseText} (${amountText})` : baseText
}

const getTransactionSecondaryText = (transaction: FinancialTransaction) => {
  const subject =
    (transaction.metadata?.investorTokenAssetId &&
      `Token Asset ${shortenValue(transaction.metadata.investorTokenAssetId)}`) ||
    (transaction.metadata?.stationLeaseAssetId &&
      `Lease Asset ${shortenValue(transaction.metadata.stationLeaseAssetId)}`) ||
    (transaction.reference && `Ref ${shortenValue(transaction.reference)}`) ||
    (transaction.accountId && `Acct ${shortenValue(transaction.accountId)}`)

  const dateText = transaction.createdAt ? TimeConverter(transaction.createdAt) : undefined
  return [subject, dateText].filter(Boolean).join(' · ')
}

const PoolState: React.FC = () => {
  const { tab } = useParams()
  const activeTab = tab as PoolTab
  const isValidTab = activeTab === 'assigned' || activeTab === 'unassigned'

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pool, setPool] = useState<TokenPoolSnapshot | null>(null)

  const [assignedLoading, setAssignedLoading] = useState(false)
  const [assignedError, setAssignedError] = useState<string | null>(null)
  const [assignedRows, setAssignedRows] = useState<plpType[]>([])
  const [assignedPageNumber, setAssignedPageNumber] = useState(1)
  const [assignedPageSize] = useState(20)
  const [assignedPagination, setAssignedPagination] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({ pageNumber: 1, pageSize: 20, total: 0 })
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [transactionsError, setTransactionsError] = useState<string | null>(null)
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([])

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  const fetchPool = async () => {
    setLoading(true)
    setErrorMessage(null)
    try {
      const response = await tokenPoolService.get()
      setPool((response?.data?.data ?? null) as TokenPoolSnapshot | null)
    } catch (e) {
      setPool(null)
      setErrorMessage(getErrorMessage(e) ?? 'Failed to load token pool')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    fetchPool()
  }, [])

  useEffect(() => {
    const fetchRecentTransactions = async () => {
      setTransactionsLoading(true)
      setTransactionsError(null)
      try {
        const response = await financialTransactionsService.list({
          pageNumber: 1,
          pageSize: 4,
          sortOrder: 'DESC',
          tokenAssetAndBlockchainOnly: true,
        })
        const data = response?.data?.data
        setTransactions(
          (data?.dataset.filter(
            (r: FinancialTransaction, index: number) => r.reference && index < 4,
          ) ?? []) as FinancialTransaction[],
        )
      } catch (e) {
        setTransactions([])
        setTransactionsError(getErrorMessage(e) ?? 'Failed to load recent transactions')
      } finally {
        setTransactionsLoading(false)
      }
    }

    fetchRecentTransactions()
  }, [])

  const fetchAssignedAssets = async (tab: PoolTab) => {
    setAssignedLoading(true)
    setAssignedError(null)
    try {
      const response = await plpService.index(
        assignedPageNumber,
        'DESC',
        'ALL',
        undefined,
        undefined,
        tab,
      )
      const data = response?.data?.data
      setAssignedRows((data?.dataset ?? []) as plpType[])
      setAssignedPagination({
        pageNumber: Number(data?.pageNumber ?? assignedPageNumber),
        pageSize: Number(data?.pageSize ?? assignedPageSize),
        total: Number(data?.total ?? 0),
      })
    } catch (e) {
      setAssignedRows([])
      setAssignedPagination({
        pageNumber: assignedPageNumber,
        pageSize: assignedPageSize,
        total: 0,
      })
      setAssignedError(getErrorMessage(e) ?? 'Failed to load assigned assets')
    } finally {
      setAssignedLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'assigned') {
      fetchAssignedAssets('assigned')
    } else {
      fetchAssignedAssets('unassigned')
    }
  }, [activeTab, assignedPageNumber, assignedPageSize])

  const poolUtilizationPct =
    pool != null && pool.totalTokens > 0
      ? Math.round((pool.soldTokens / pool.totalTokens) * 100)
      : null

  if (!isValidTab) return <Navigate to='/blockchain/pool-state/assigned' replace />

  const assignedTotalPages = Math.max(
    1,
    Math.ceil(assignedPagination.total / Math.max(1, assignedPagination.pageSize)),
  )




  const availableAmount =
    (pool?.availableTokens || 0) * (pool?.tokenValueNairaMajor || 0);

  const soldPercentage =
    pool?.soldTokens && pool?.totalTokens
      ? ((pool.soldTokens / pool.totalTokens) * 100).toFixed(1)
      : '0';

  return (
    <div className='admin-pool'>
      <div className='admin-pool__title'>Investment Pool State</div>

      <div className='row g-3 g-xl-4'>
        <div className='col-12 col-md-3'>
          <div className='admin-pool-kpi admin-pool-kpi--blue'>
            <div className='admin-pool-kpi__label'>Total Pool Value</div>
            <div className='admin-pool-kpi__value'>
              {loading ? 'Loading...' : errorMessage ? '-' : pool?.totalTokens}
            </div>
          </div>
        </div>
        <div className='col-12 col-md-3'>
          <div className='admin-pool-kpi admin-pool-kpi--green'>
            <div className='admin-pool-kpi__label'>Pool Invested</div>
            <div className='admin-pool-kpi__value'>
              {loading ? 'Loading...' : errorMessage ? '-' : pool?.soldTokens}
            </div>
          </div>
        </div>
        <div className='col-12 col-md-3'>
          <div className='admin-pool-kpi admin-pool-kpi--orange'>
            <div className='admin-pool-kpi__label'>Pool Available</div>
            <div className='admin-pool-kpi__value'>
              {loading ? 'Loading...' : errorMessage ? '-' : pool?.availableTokens}
            </div>
          </div>
        </div>
        <div className='col-12 col-md-3'>
          <div className='admin-pool-kpi admin-pool-kpi--orange'>
            <div className='admin-pool-kpi__label'>Available Token Value</div>
            <div className='admin-pool-kpi__value'>
              {loading
                ? 'Loading...'
                : errorMessage
                  ? '-'
                  : formatNairaMajor(
                      Number(pool?.tokenValueNairaMajor) * Number(pool?.availableTokens),
                    )}
            </div>
          </div>
        </div>
      </div>

      <div className='row g-3 g-xl-4 mt-2'>
        <div className='col-12 col-xl-6'>


           {/* Pool Health Section */}
          <section className="pool-health-section mb-2">
            <div className="card p-4 text-center">
            <div className='admin-pool-card__title text-start'>Pool Status</div> 
              <div className="row">
                <div className="col-6 text-start">
                  <small>Sold {soldPercentage}%</small>
                </div>
                <div className="col-6 text-end">
                  <small>{100 - Number(soldPercentage)}% Available</small>
                </div>
              </div>
              <div className="health-indicator position-relative w-100 d-flex align-items-center justify-content-center rounded-3 overflow-hidden">
                <div
                  className="health-bg position-absolute h-100"
                  style={{
                    width: `${soldPercentage}%`,
                  }}
                  title="sold"
                ></div>
                <div className="health-content position-relative z-1 py-4">
                  <span
                    className="health-percentage display-4 fw-bold text-white"
                    title="avaialble"
                  >
                    {soldPercentage}%
                  </span>
                </div>
              </div>
            </div>
          </section>


          <div className='admin-pool-card'>
            <div className='admin-pool-card__title'>Pool Composition</div>
            <div className='admin-pool-composition'>
              <div className='admin-pool-composition__row'>
                <span className='admin-pool-composition__label'>Total Tokens</span>
                <span className='admin-pool-composition__value'>
                  {loading ? 'Loading...' : errorMessage ? '-' : (pool?.totalTokens ?? '-')}
                </span>
              </div>
              <div className='admin-pool-composition__row'>
                <span className='admin-pool-composition__label'>Sold Tokens</span>
                <span className='admin-pool-composition__value'>
                  {loading ? 'Loading...' : errorMessage ? '-' : (pool?.soldTokens ?? '-')}
                </span>
              </div>
              <div className='admin-pool-composition__row'>
                <span className='admin-pool-composition__label'>Available Tokens</span>
                <span className='admin-pool-composition__value'>
                  {loading ? 'Loading...' : errorMessage ? '-' : (pool?.availableTokens ?? '-')}
                </span>
              </div>
              <div className='admin-pool-composition__row'>
                <span className='admin-pool-composition__label'>Token Value</span>
                <span className='admin-pool-composition__value'>
                  {loading
                    ? 'Loading...'
                    : errorMessage
                      ? '-'
                      : formatNairaMajor(pool?.tokenValueNairaMajor)}
                </span>
              </div>
              <div className='admin-pool-composition__row'>
                <span className='admin-pool-composition__label'>Pool Status</span>
                <span className='admin-pool-composition__value'>
                  {loading
                    ? 'Loading...'
                    : errorMessage
                      ? '-'
                      : poolUtilizationPct != null
                        ? `${poolUtilizationPct}%`
                        : '-'}
                </span>
              </div>
            </div>
          </div>

 
        </div>

        <div className='col-12 col-xl-6'>
          <div className='admin-pool-card'>
            <div className='admin-pool-card__title'>Recent Pool Transactions</div>
            <div className='admin-pool-transactions'>
              {transactionsLoading ? (
                <div className='admin-pool-transactions__item'>
                  <span className='admin-pool-transactions__dot admin-pool-transactions__dot--blue' />
                  <div className='admin-pool-transactions__text'>
                    <div className='admin-pool-transactions__main'>
                      Loading recent transactions...
                    </div>
                  </div>
                </div>
              ) : transactionsError ? (
                <div className='admin-pool-transactions__item'>
                  <span className='admin-pool-transactions__dot admin-pool-transactions__dot--orange' />
                  <div className='admin-pool-transactions__text'>
                    <div className='admin-pool-transactions__main'>{transactionsError}</div>
                  </div>
                </div>
              ) : transactions.length === 0 ? (
                <div className='admin-pool-transactions__item'>
                  <span className='admin-pool-transactions__dot admin-pool-transactions__dot--blue' />
                  <div className='admin-pool-transactions__text'>
                    <div className='admin-pool-transactions__main'>
                      No recent transactions found
                    </div>
                  </div>
                </div>
              ) : (
                transactions.map((transaction, index) => (
                  <React.Fragment key={transaction.id}>
                    <div className='admin-pool-transactions__item'>
                      <span
                        className={`admin-pool-transactions__dot admin-pool-transactions__dot--${getTransactionDotVariant(
                          transaction,
                        )}`}
                      />
                      <div className='admin-pool-transactions__text'>
                        <div className='admin-pool-transactions__main'>
                          {getTransactionPrimaryText(transaction)}
                        </div>
                        <div className='admin-pool-transactions__sub'>
                          {getTransactionSecondaryText(transaction)}
                        </div>
                      </div>
                    </div>
                    {index < transactions.length - 1 ? (
                      <div className='admin-pool-transactions__divider' />
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {!loading && errorMessage ? <div className='mt-3'>{errorMessage}</div> : null}

      <div className='admin-pool__tabs'>
        <Link
          to='/blockchain/pool-state/assigned'
          className={['admin-pool__tab', activeTab === 'assigned' ? 'admin-pool__tab--active' : '']
            .filter(Boolean)
            .join(' ')}
        >
          Assigned Assets
        </Link>
        <Link
          to='/blockchain/pool-state/unassigned'
          className={[
            'admin-pool__tab',
            activeTab === 'unassigned' ? 'admin-pool__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          Unassigned Assets
        </Link>
      </div>

      <div className='admin-pool-table'>
        {activeTab === 'assigned' ? (
          <>
            <div className='admin-pool-table__head admin-pool-table__grid--assigned'>
              <div>S/N</div>
              <div>Station Name</div>
              <div>Location</div>
              <div>Asset Value</div>
              <div>Valuation Date</div>
              <div className='text-end'>Actions</div>
            </div>
            <div className='admin-pool-table__body'>
              {assignedLoading && (
                <div className='admin-pool-table__row admin-pool-table__grid--assigned'>
                  <div className='admin-pool-table__cell'>Loading...</div>
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                </div>
              )}

              {!assignedLoading && assignedError && (
                <div className='admin-pool-table__row admin-pool-table__grid--assigned'>
                  <div className='admin-pool-table__cell'>{assignedError}</div>
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                </div>
              )}

              {!assignedLoading && !assignedError && assignedRows.length === 0 && (
                <div className='admin-pool-table__row admin-pool-table__grid--assigned'>
                  <div className='admin-pool-table__cell'>No assigned asset found.</div>
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                </div>
              )}

              {!assignedLoading &&
                !assignedError &&
                assignedRows.map((r, idx) => {
                  const sn =
                    (assignedPagination.pageNumber - 1) * assignedPagination.pageSize + idx + 1
                  const valuationDate = r.readyForReviewAt || r.createdAtISO8601
                  return (
                    <div
                      key={r.id}
                      className='admin-pool-table__row admin-pool-table__grid--assigned'
                    >
                      <div className='admin-pool-table__cell'>{sn}</div>
                      <div className='admin-pool-table__cell admin-pool-table__cell--strong'>
                        {r.companyName}
                      </div>
                      <div className='admin-pool-table__cell'>{r.companyAddress}</div>
                      <div className='admin-pool-table__cell'>
                        {formatNairaMajor(r.stationLeaseValuationMajor)}
                      </div>
                      <div className='admin-pool-table__cell'>{TimeConverter(valuationDate)}</div>

                      <div className='admin-pool-table__cell text-end'>
                        <Link to={`/blockchain/traders-card/ALL/${r.id}`}>
                          <button
                            type='button'
                            className='admin-pool-table__more'
                            aria-label='Actions'
                          >
                            View
                          </button>
                        </Link>
                      </div>
                    </div>
                  )
                })}
            </div>

            <div className='admin-pool-pagination'>
              <button
                type='button'
                className='admin-pool-pagination__button'
                onClick={() => setAssignedPageNumber((p) => Math.max(1, p - 1))}
                disabled={assignedPageNumber <= 1 || assignedLoading}
              >
                Previous
              </button>
              <div className='admin-pool-pagination__numbers'>
                Page {assignedPageNumber} of {assignedTotalPages}
              </div>
              <button
                type='button'
                className='admin-pool-pagination__button'
                onClick={() => setAssignedPageNumber((p) => Math.min(assignedTotalPages, p + 1))}
                disabled={assignedPageNumber >= assignedTotalPages || assignedLoading}
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <>
            <div className='admin-pool-table__head admin-pool-table__grid--assigned'>
              <div>S/N</div>
              <div>Station Name</div>
              <div>Location</div>
              <div>Asset Value</div>
              <div>Date</div>
              <div className='text-end'>Actions</div>
            </div>
            <div className='admin-pool-table__body'>
              {assignedLoading && (
                <div className='admin-pool-table__row admin-pool-table__grid--assigned'>
                  <div className='admin-pool-table__cell'>Loading...</div>
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                </div>
              )}

              {!assignedLoading && assignedError && (
                <div className='admin-pool-table__row admin-pool-table__grid--assigned'>
                  <div className='admin-pool-table__cell'>{assignedError}</div>
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                </div>
              )}

              {!assignedLoading && !assignedError && assignedRows.length === 0 && (
                <div className='admin-pool-table__row admin-pool-table__grid--assigned'>
                  <div className='admin-pool-table__cell'>No assigned asset found.</div>
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                  <div className='admin-pool-table__cell' />
                </div>
              )}
              {!assignedLoading &&
                !assignedError &&
                assignedRows.map((r, idx) => {
                  const sn =
                    (assignedPagination.pageNumber - 1) * assignedPagination.pageSize + idx + 1
                  const valuationDate = r.readyForReviewAt || r.createdAtISO8601
                  return (
                    <div
                      key={r.id}
                      className='admin-pool-table__row admin-pool-table__grid--assigned'
                    >
                      <div className='admin-pool-table__cell'>{sn}</div>
                      <div className='admin-pool-table__cell admin-pool-table__cell--strong'>
                        {r.companyName}
                      </div>
                      <div className='admin-pool-table__cell'>{r.companyAddress}</div>
                      <div className='admin-pool-table__cell'>
                        {formatNairaMajor(r.stationLeaseValuationMajor)}
                      </div>
                      <div className='admin-pool-table__cell'>{TimeConverter(valuationDate)}</div>
                      <div className='admin-pool-table__cell text-end'>
                        <Link to={`/blockchain/traders-card/ALL/${r.id}`}>
                          <button
                            type='button'
                            className='admin-pool-table__more'
                            aria-label='Actions'
                          >
                            View
                          </button>
                        </Link>
                      </div>
                    </div>
                  )
                })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PoolState
